import { parseDecimal, roundToCurrencyPrecision } from '@insio/decimal';
import type { CurrencyCode } from '@insio/shared-types';

/** Minimal shape: anything carrying an optional issue `meta` record. */
interface WithMeta {
  meta?: unknown;
}

/** Read a string-coerced value from an issue's `meta` by key, or `null`. */
export function readIssueMeta(issue: WithMeta, key: string): string | null {
  const meta = issue.meta;
  if (!meta || typeof meta !== 'object' || !(key in meta)) return null;
  const value = (meta as Record<string, unknown>)[key];
  if (value == null) return null;
  return String(value);
}

/** Read `meta.expected` as a display/apply string, or `null` when absent. */
export function getIssueExpected(issue: WithMeta): string | null {
  return readIssueMeta(issue, 'expected');
}

/** Round a numeric expected value to the currency's decimal places; non-numeric
 * values (e.g. VAT IDs) pass through unchanged. */
export function roundExpectedToCurrency(
  expected: string,
  currencyCode?: CurrencyCode | null
): string {
  const parsed = parseDecimal(expected);
  if (parsed == null) return expected;
  return roundToCurrencyPrecision(parsed, currencyCode ?? undefined).toString();
}

/** Return a copy of `issue` with `meta.expected` rounded to the currency's
 * decimal places, so both the displayed message and the apply affordance use a
 * clean value. Returns the issue unchanged when there is nothing to round. */
export function withRoundedExpected<T extends WithMeta>(
  issue: T,
  currencyCode?: CurrencyCode | null
): T {
  const expected = getIssueExpected(issue);
  if (expected == null) return issue;
  const rounded = roundExpectedToCurrency(expected, currencyCode);
  if (rounded === expected) return issue;
  return { ...issue, meta: { ...(issue.meta as object), expected: rounded } };
}
